import { useCallback, useEffect, useRef, useState } from 'react'
import { buildDetectors } from './detectorRegistry'
import { paintOverlay } from './overlayPainter'
import {
    acquireCameraStream,
    stopCameraStream,
    listCameraOptions,
    MOBILE_CAMERA_ID,
} from './cameraConfig'

export default function useVisionLoop(initialId = 'face') {
    const videoRef = useRef(null)
    const overlayRef = useRef(null)

    const detectorsRef = useRef(null)
    if (!detectorsRef.current) {
        detectorsRef.current = {}
        buildDetectors().forEach((d) => {
            detectorsRef.current[d.id] = d
        })
    }

    const activeIdRef = useRef(initialId)
    const latestRef = useRef({ boxes: [], insights: [] })
    const busyRef = useRef(false)
    const rafRef = useRef(null)
    const streamRef = useRef(null)
    const stampRef = useRef(0)
    const deviceIdRef = useRef('')
    const mirroredRef = useRef(true)
    const fpsCountRef = useRef(0)
    const fpsStampRef = useRef(performance.now())

    const [activeId, setActiveIdState] = useState(initialId)
    const [insights, setInsights] = useState([])
    const [insightsTitle, setInsightsTitle] = useState('AI Analysis Insights')
    const [fps, setFps] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [mirrored, setMirrored] = useState(true)
    const [devices, setDevices] = useState([])
    const [activeDeviceId, setActiveDeviceId] = useState('')

    const loadDetector = useCallback(async (id) => {
        const detector = detectorsRef.current[id]
        if (!detector) return
        setLoading(true)
        try {
            await detector.load()
            detector.reset()
        } catch (e) {
            setError('Failed to load model. Check your connection and retry.')
        } finally {
            setLoading(false)
        }
    }, [])

    const setActiveId = useCallback(
        (id) => {
            if (!detectorsRef.current[id]) return
            activeIdRef.current = id
            latestRef.current = { boxes: [], insights: [] }
            setInsights([])
            setInsightsTitle('AI Analysis Insights')
            setActiveIdState(id)
            loadDetector(id)
        },
        [loadDetector]
    )

    const refreshDevices = useCallback(async () => {
        try {
            // Includes a "Phone Camera" option (paired via Configure > Inputs).
            setDevices(await listCameraOptions())
        } catch (e) {
            /* ignore */
        }
    }, [])

    const startCamera = useCallback(
        async (deviceId) => {
            try {
                setError('')
                // Acquire the new stream FIRST; only stop the old one on success
                // so a failure never leaves a dead feed.
                const stream = await acquireCameraStream(deviceId)
                const prev = streamRef.current
                streamRef.current = stream
                if (prev && prev !== stream) stopCameraStream(prev)
                const el = videoRef.current
                if (el) {
                    el.srcObject = stream
                    await el.play().catch(() => {})
                }

                const isMobile = deviceId === MOBILE_CAMERA_ID
                const settings =
                    stream.getVideoTracks()[0]?.getSettings?.() || {}
                deviceIdRef.current = isMobile
                    ? MOBILE_CAMERA_ID
                    : settings.deviceId || deviceId || ''
                setActiveDeviceId(deviceIdRef.current)
                // Record the active source so effectiveAspect() knows the phone
                // is in use and applies the portrait/landscape rotation (bug 1).
                sessionStorage.setItem('aiCameraDeviceId', deviceIdRef.current)
                // Rear/phone cameras aren't mirrored; default to mirrored for a
                // user-facing local webcam.
                const nextMirror = isMobile
                    ? false
                    : settings.facingMode
                      ? settings.facingMode === 'user'
                      : true
                mirroredRef.current = nextMirror
                setMirrored(nextMirror)
                // Labels are only populated after permission is granted.
                refreshDevices()
            } catch (e) {
                // No local webcam (or denied): keep the device list populated
                // so "Phone Camera" is still selectable — don't dead-end.
                refreshDevices()
                setError(
                    'No local camera. Pick "Phone Camera" to use your phone.'
                )
            }
        },
        [refreshDevices]
    )

    const selectDevice = useCallback(
        (deviceId) => {
            if (deviceId === deviceIdRef.current) return
            deviceIdRef.current = deviceId
            startCamera(deviceId)
        },
        [startCamera]
    )

    // Force a re-attach of the phone feed even when the phone is already the
    // active device — on RECONNECT the stream object is brand-new, so the
    // selectDevice "same device" guard would otherwise leave the old, dead
    // stream on the <video> (blank).
    const reattachMobile = useCallback(() => {
        deviceIdRef.current = MOBILE_CAMERA_ID
        startCamera(MOBILE_CAMERA_ID)
    }, [startCamera])

    // Flip the preview (and detectors' frame grab) horizontally.
    const toggleMirror = useCallback(() => {
        const next = !mirroredRef.current
        mirroredRef.current = next
        setMirrored(next)
    }, [])

    // Set the mirror to an explicit state (used by OCR to force it off so text
    // reads in the correct orientation).
    const setMirror = useCallback((next) => {
        if (mirroredRef.current === next) return
        mirroredRef.current = next
        setMirrored(next)
    }, [])

    useEffect(() => {
        // Populate the device list immediately (incl. "Phone Camera") so the
        // picker works even before/without a local camera.
        refreshDevices()
        startCamera()
        loadDetector(activeIdRef.current)

        const onDeviceChange = () => refreshDevices()
        navigator.mediaDevices?.addEventListener?.(
            'devicechange',
            onDeviceChange
        )

        const tick = () => {
            rafRef.current = requestAnimationFrame(tick)
            const video = videoRef.current
            if (!video || video.readyState < 2) return

            const vw = video.videoWidth
            const vh = video.videoHeight
            const detector = detectorsRef.current[activeIdRef.current]

            if (detector && !busyRef.current) {
                busyRef.current = true
                const stamp = ++stampRef.current
                Promise.resolve(
                    detector.detect({
                        video,
                        vw,
                        vh,
                        stamp,
                        mirrored: mirroredRef.current,
                    })
                )
                    .then((res) => {
                        if (res && activeIdRef.current === detector.id) {
                            latestRef.current = res
                            setInsights(res.insights || [])
                            setInsightsTitle(
                                res.title || 'AI Analysis Insights'
                            )
                            fpsCountRef.current += 1
                        }
                    })
                    .catch(() => {})
                    .finally(() => {
                        busyRef.current = false
                    })
            }

            paintOverlay(
                overlayRef.current,
                video,
                latestRef.current.boxes,
                detector && detector.accent,
                mirroredRef.current
            )

            const now = performance.now()
            const elapsed = now - fpsStampRef.current
            if (elapsed >= 500) {
                setFps(Math.round((fpsCountRef.current * 1000) / elapsed))
                fpsCountRef.current = 0
                fpsStampRef.current = now
            }
        }
        rafRef.current = requestAnimationFrame(tick)

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
            navigator.mediaDevices?.removeEventListener?.(
                'devicechange',
                onDeviceChange
            )
            stopCameraStream(streamRef.current)
            Object.values(detectorsRef.current).forEach((d) => d.dispose())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        videoRef,
        overlayRef,
        activeId,
        setActiveId,
        insights,
        insightsTitle,
        fps,
        loading,
        error,
        mirrored,
        toggleMirror,
        setMirror,
        devices,
        activeDeviceId,
        selectDevice,
        reattachMobile,
    }
}
